import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isAfter, isValid as isValidDate, startOfDay } from 'date-fns';

import { FileFormat, FileSizeFormat } from '../enums';
import { convertToFileSizeFormat } from '../helpers';
import {
  EMAIL_REGEXP,
  FORBIDDEN_SYMBOLS_WITH_NO_SPACES_REGEX,
  FORBIDDEN_SYMBOLS_WITH_SPACES_REGEX,
  OPERATOR_KEY_VALIDATOR_PATTERN,
} from './constants/patterns.constant';
import { ValidatorsKeys } from './enums/validators.enum';
import {
  FileUploadExtensionValidatorCallbackParams,
  FileUploadSizeValidatorCallbackParams,
  MinMaxLengthValidatorCallbackParams,
  RangeValidatorCallbackParams,
} from './interfaces/validator.interface';

export const emailValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const email = control.value;

    if (email && !email.match(EMAIL_REGEXP)) {
      return { [ValidatorsKeys.Email]: true };
    }

    return null;
  };
};

export const forbiddenSymbols = (isNoSpaces?: boolean): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value || '';
    const forbiddenPattern = isNoSpaces ? FORBIDDEN_SYMBOLS_WITH_NO_SPACES_REGEX : FORBIDDEN_SYMBOLS_WITH_SPACES_REGEX;

    if (value.match(forbiddenPattern)) {
      return { [ValidatorsKeys.ForbiddenSymbols]: true };
    }

    return null;
  };
};

export const fileSizeValidator = (maxAllowedSize: number, format: FileSizeFormat): ValidatorFn => {
  const convertedToBytesMaxSize = convertToFileSizeFormat(maxAllowedSize, format, FileSizeFormat.B);
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value && control.value.size > convertedToBytesMaxSize) {
      return {
        [ValidatorsKeys.LargeSize]: {
          requiredSize: maxAllowedSize,
          actualSize: convertToFileSizeFormat(control.value.size, FileSizeFormat.B, format),
          format,
        } as FileUploadSizeValidatorCallbackParams,
      };
    }

    return null;
  };
};

export const requiredTrimmedValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const trimmed = value != null ? String(value).trim() : '';

    return trimmed ? null : { required: true };
  };
};

export const uniqueNameValidator = (excludedNames: string[]): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value && typeof value === 'string' && excludedNames.some((name) => name.toLowerCase() === value.toLowerCase())) {
      return {
        [ValidatorsKeys.UniqueName]: true,
      };
    }

    return null;
  };
};

// A 0 is a legitimate bound, so emptiness cannot be a truthiness check.
const isEmptyControlValue = (value: unknown): boolean => value === null || value === undefined || value === '';

// Without this guard an empty sibling coerces to 0 (+null === 0), so a filled control reports a false
// order error while the other bound is still blank.
const areComparable = (control: AbstractControl, sibling: AbstractControl): boolean =>
  sibling.enabled && !isEmptyControlValue(control.value) && !isEmptyControlValue(sibling.value);

/**
 * Fails when the control value is greater than the compared max control value.
 * `isStrict` also rejects an equal pair, for contracts that require min strictly less than max.
 */
export const minGreaterThanMaxValidator = (maxControl: AbstractControl, isStrict = false): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!areComparable(control, maxControl)) {
      return null;
    }

    const isInvalid = isStrict ? +control.value >= +maxControl.value : +control.value > +maxControl.value;

    return isInvalid ? { [ValidatorsKeys.MinGreaterThanMax]: true } : null;
  };
};

/**
 * Fails when the control value is less than the compared min control value.
 * `isStrict` also rejects an equal pair, for contracts that require max strictly greater than min.
 */
export const maxLessThanMinValidator = (minControl: AbstractControl, isStrict = false): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!areComparable(control, minControl)) {
      return null;
    }

    const isInvalid = isStrict ? +control.value <= +minControl.value : +control.value < +minControl.value;

    return isInvalid ? { [ValidatorsKeys.MaxLessThanMin]: true } : null;
  };
};

/**
 * Fails when the control date is later than the compared control date. Both dates are reduced to their local
 * calendar day, matching what the datepicker renders — the raw values mix API timestamps with UTC midnights.
 * Bypasses empty and unparsable values on either side.
 */
export const dateNotAfterValidator = (compareControl: AbstractControl): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || !compareControl.value) {
      return null;
    }

    const date = new Date(control.value);
    const comparedDate = new Date(compareControl.value);

    if (!isValidDate(date) || !isValidDate(comparedDate)) {
      return null;
    }

    return isAfter(startOfDay(date), startOfDay(comparedDate)) ? { [ValidatorsKeys.DateAfterCompared]: true } : null;
  };
};

export const fileFormatValidator =
  (allowedFormats: FileFormat[]): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const file: File | null = control.value;

    if (!file) return null;

    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const isValid = extension !== null && allowedFormats.includes(extension as FileFormat);

    return isValid
      ? null
      : {
          [ValidatorsKeys.Format]: {
            format: allowedFormats,
          } as FileUploadExtensionValidatorCallbackParams,
        };
  };

export const urlValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const validationError = { [ValidatorsKeys.InvalidUrl]: true };

    if (!value) {
      return null;
    }

    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return validationError;
    }

    return url.protocol === 'http:' || url.protocol === 'https:' ? null : validationError;
  };
};

export const rangeValidator =
  (minRaw: string, maxRaw: string): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const rawValue = (control.value ?? '').toString().trim();
    const isDecimalRange = minRaw.includes('.') || maxRaw.includes('.');
    const min = parseFloat(minRaw);
    const max = parseFloat(maxRaw);

    const buildError = (): ValidationErrors => ({
      [ValidatorsKeys.Range]: {
        min: minRaw,
        max: maxRaw,
      } as RangeValidatorCallbackParams,
    });

    const decimalPlacesAllowed = Math.max((minRaw.split('.')[1] || '').length, (maxRaw.split('.')[1] || '').length);
    const validNumberPattern = isDecimalRange ? new RegExp(`^\\d+(\\.\\d{1,${decimalPlacesAllowed}})?$`) : /^\d+$/;

    if (!validNumberPattern.test(rawValue)) {
      return buildError();
    }

    const value = parseFloat(rawValue);
    return value >= min && value <= max ? null : buildError();
  };

/**
 * Numeric counterpart of `rangeValidator`: bypasses empty values (optional controls),
 * rejects non-numeric input, and can allow a standalone `0` outside the range.
 */
export const numberRangeValidator =
  (min: number, max: number, allowZero = false): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const rawValue = typeof control.value === 'string' ? control.value.trim() : control.value;

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    const value = Number(rawValue);
    const isValid = !Number.isNaN(value) && ((allowZero && value === 0) || (value >= min && value <= max));

    return isValid ? null : { [ValidatorsKeys.Range]: { min: String(min), max: String(max) } as RangeValidatorCallbackParams };
  };

export const notDefaultValueValidator = (defaultValue: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.trim();
    return value && value.toLowerCase() !== defaultValue.toLowerCase() ? null : { [ValidatorsKeys.NonDefaultName]: true };
  };
};

export const operatorKeyValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }

    return OPERATOR_KEY_VALIDATOR_PATTERN.test(value) ? null : { [ValidatorsKeys.InvalidOperatorKeyFormat]: true };
  };
};

export const maxLengthValidator = (maxLength: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (typeof control.value === 'object') {
      return null;
    }

    const value = String(control.getRawValue());

    return value.length > maxLength
      ? { [ValidatorsKeys.MaxLength]: { requiredLength: maxLength, actualLength: value.length } as MinMaxLengthValidatorCallbackParams }
      : null;
  };
};

export const crossFieldsWithArraysOverlappingValidator = (otherControlName: string, otherControlLabel: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const otherControl = control.parent?.get(otherControlName);
    const value: string[] = control.value;
    const otherValue: string[] = otherControl?.value;

    if (!value || !otherValue) {
      return null;
    }

    const otherValueSet = new Set(otherValue);
    const overlappingValues = value.filter((item) => otherValueSet.has(item));

    if (!overlappingValues.length) {
      return null;
    }

    return { [ValidatorsKeys.ArrayOverlapping]: otherControlLabel };
  };
};

export const jsonValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(value);

      if (typeof parsedValue !== 'object' || parsedValue === null) {
        return { [ValidatorsKeys.InvalidJson]: true };
      }

      return null;
    } catch (e) {
      return { [ValidatorsKeys.InvalidJson]: true };
    }
  };
};
